// components/ImageUploader.jsx
// STEP 4: Reusable drag-and-drop image uploader component
//
// USAGE:
//   <ImageUploader
//     currentImage="/uploads/product_abc.jpg"   ← existing image path (or null)
//     onUploaded={(path) => setForm(p => ({...p, imageUrl: path}))}
//     prefix="product"
//   />

import { useState, useRef } from "react";
import { uploadService } from "../services/api";

const ImageUploader = ({
  currentImage = null, // existing image URL path from DB
  onUploaded, // callback(path) called after successful upload
  prefix = "img", // "product" | "category" | "img"
  label = "Product Image",
  width = "100%",
  height = 200,
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(
    currentImage ? uploadService.getUrl(currentImage) : null,
  );
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  // ── Handle file selected (from input or drop) ──────────────────────────────
  const handleFile = async (file) => {
    if (!file) return;

    // Client-side validation
    const allowed = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/gif",
    ];
    if (!allowed.includes(file.type)) {
      setError("Only JPG, PNG, WebP, GIF files are allowed.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("File is too large. Maximum size is 5 MB.");
      return;
    }

    setError("");
    setUploading(true);

    // Show local preview immediately (before upload completes)
    const localPreviewUrl = URL.createObjectURL(file);
    setPreview(localPreviewUrl);

    try {
      // POST /api/Upload/image
      const path = await uploadService.upload(file, prefix);
      // path = "/uploads/product_20240315_abc.jpg"

      // Update preview to the real server URL
      setPreview(uploadService.getUrl(path));

      // Tell parent component the new path to save
      onUploaded?.(path);
    } catch (e) {
      setError(e.message || "Upload failed.");
      setPreview(currentImage ? uploadService.getUrl(currentImage) : null);
    } finally {
      setUploading(false);
      // Release the object URL to free memory
      URL.revokeObjectURL(localPreviewUrl);
    }
  };

  const onInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const clearImage = () => {
    setPreview(null);
    onUploaded?.(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div style={{ width }}>
      {/* Label */}
      <label
        style={{
          display: "block",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 1,
          textTransform: "uppercase",
          color: "var(--stone)",
          marginBottom: 8,
        }}
      >
        {label}
      </label>

      {/* Drop zone */}
      <div
        onClick={() => !uploading && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        style={{
          width: "100%",
          height,
          border: `2px dashed ${dragOver ? "var(--emerald)" : "var(--mist)"}`,
          borderRadius: 14,
          background: dragOver ? "var(--foam)" : "var(--snow)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: uploading ? "not-allowed" : "pointer",
          overflow: "hidden",
          position: "relative",
          transition: "all .2s",
        }}
      >
        {/* Current image preview */}
        {preview ? (
          <>
            <img
              src={preview}
              alt="Preview"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            {/* Overlay on hover */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(0,0,0,0)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                transition: "background .2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(0,0,0,0.45)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(0,0,0,0)";
              }}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  inputRef.current?.click();
                }}
                style={{
                  background: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "7px 14px",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  fontFamily: "var(--sans)",
                  opacity: 0,
                  transition: "opacity .2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
              >
                ✏️ Change
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  clearImage();
                }}
                style={{
                  background: "#dc2626",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "7px 12px",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  fontFamily: "var(--sans)",
                  opacity: 0,
                  transition: "opacity .2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
              >
                🗑️ Remove
              </button>
            </div>
          </>
        ) : (
          /* Empty state */
          <div
            style={{ textAlign: "center", padding: 20, pointerEvents: "none" }}
          >
            {uploading ? (
              <>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    border: "3px solid var(--mist)",
                    borderTopColor: "var(--emerald)",
                    borderRadius: "50%",
                    animation: "spin .7s linear infinite",
                    margin: "0 auto 10px",
                  }}
                />
                <p
                  style={{
                    fontSize: 13,
                    color: "var(--stone)",
                    fontWeight: 500,
                  }}
                >
                  Uploading…
                </p>
              </>
            ) : (
              <>
                <div style={{ fontSize: 36, marginBottom: 8 }}>🖼️</div>
                <p
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "var(--pine)",
                    marginBottom: 4,
                  }}
                >
                  {dragOver ? "Drop image here" : "Click or drag image here"}
                </p>
                <p style={{ fontSize: 12, color: "var(--stone)" }}>
                  JPG, PNG, WebP or GIF · max 5 MB
                </p>
              </>
            )}
          </div>
        )}

        {/* Upload progress overlay */}
        {uploading && preview && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(255,255,255,0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                border: "3px solid var(--mist)",
                borderTopColor: "var(--emerald)",
                borderRadius: "50%",
                animation: "spin .7s linear infinite",
              }}
            />
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p
          style={{
            fontSize: 12,
            color: "#dc2626",
            marginTop: 6,
            fontWeight: 500,
          }}
        >
          ⚠️ {error}
        </p>
      )}

      {/* Current image path (small info text) */}
      {preview && !uploading && (
        <p
          style={{
            fontSize: 11,
            color: "var(--stone)",
            marginTop: 5,
            wordBreak: "break-all",
          }}
        >
          ✅ Image uploaded
        </p>
      )}

      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
        onChange={onInputChange}
        style={{ display: "none" }}
      />
    </div>
  );
};

export default ImageUploader;
